import { useEffect, useMemo, type ReactNode } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { deDE as coreDeDE } from '@mui/material/locale';
import { deDE as gridDeDE } from '@mui/x-data-grid-pro/locales';
import { deDE as pickersDeDE } from '@mui/x-date-pickers/locales';
import { useLocale } from '../i18n/localeContext';
import { createAppTheme } from './theme';
import {
    colorModeValue,
    DEFAULT_COLOR_MODE,
    storeColorMode
} from './colorMode';
import { useStoredValue } from '../utils/useStoredValue';
import { ColorModeContext } from './colorModeContext';

/**
 * Owns the two things the theme is built from — the appearance (light/dark) and
 * the language — and rebuilds it when either changes.
 *
 * **Appearance**: the user's stored choice, or the device's own setting while
 * they have never made one, so nothing is persisted until they actually switch.
 * `prefers-color-scheme` is watched rather than read once, so a tablet flipping
 * to dark at dusk takes the app with it.
 *
 * **Language**: matches MUI's own component text (grid menus/pagination, picker
 * labels) to what react-intl is using. English is MUI's built-in default, so
 * only non-default locales need a bundle. Read rather than resolved once at
 * startup, so the switcher takes MUI's text with it instead of leaving the grid
 * in the old language until a reload.
 */
export function LocalizedThemeProvider({ children }: { children: ReactNode }) {
    const { locale } = useLocale();
    // The stored choice IS the state — no copy in the component, so switching
    // the appearance in one window takes the others with it (see
    // useStoredValue) instead of leaving them light until a reload.
    const mode = useStoredValue(colorModeValue) ?? DEFAULT_COLOR_MODE;
    const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');
    const resolvedMode =
        mode === 'system' ? (prefersDark ? 'dark' : 'light') : mode;

    const colorMode = useMemo(
        () => ({
            mode,
            resolvedMode,
            // Writing is all there is to do: the write announces itself,
            // and the read above follows.
            setMode: storeColorMode
        }),
        [mode, resolvedMode]
    );

    const theme = useMemo(() => {
        const base = createAppTheme(resolvedMode);
        return locale === 'de'
            ? createTheme(base, coreDeDE, gridDeDE, pickersDeDE)
            : base;
    }, [locale, resolvedMode]);

    /**
     * Tint the browser/OS chrome around the app to match its own app bar. The
     * manifest's theme_color is fixed at install time and can't follow a runtime
     * switch, so the meta tag — which can — is kept in step here. Matters most
     * installed on a tablet, where that strip frames the whole screen.
     */
    useEffect(() => {
        const color =
            resolvedMode === 'dark'
                ? theme.palette.background.paper
                : theme.palette.primary.main;
        let meta = document.querySelector<HTMLMetaElement>(
            'meta[name="theme-color"]'
        );
        if (!meta) {
            meta = document.createElement('meta');
            meta.name = 'theme-color';
            document.head.appendChild(meta);
        }
        meta.content = color;
    }, [resolvedMode, theme]);

    return (
        <ColorModeContext.Provider value={colorMode}>
            <ThemeProvider theme={theme}>{children}</ThemeProvider>
        </ColorModeContext.Provider>
    );
}
